/**
 * Fait : la partie LE PROJET de sa fiche — son nom, sa description, qui est
 *   là, le formulaire qui permet de modifier les deux, et les trois gestes du
 *   propriétaire : archiver, désarchiver, supprimer. Nom et description
 *   partent ENSEMBLE, dans une seule demande, donc sous un seul arbitrage de
 *   version.
 *
 *   POURQUOI ARCHIVER N'A PAS DE FENÊTRE ET SUPPRIMER EN A UNE : archiver se
 *   défait d'un clic — le bouton d'à côté le dit et le fait. Supprimer, non :
 *   le projet quitte la vue de tous, et personne dans la famille ne peut l'en
 *   sortir. Mettre l'avertissement partout l'userait, et plus personne ne le
 *   lirait là où il compte.
 *
 * Ne fait pas : elle n'appelle pas l'API — le parent le fait, rejoue le
 *   conflit et recharge — et ne décide d'aucun droit : elle reçoit ce qu'elle
 *   a le droit d'afficher.
 *
 *   Pourquoi ce fichier existe, écrit le 2026-08-04 : la fiche n'offrait
 *   qu'un bouton « Renommer le projet » qui ajoutait « (modifié) » au nom —
 *   une commodité de test restée en place. Et surtout, aucun écran ne
 *   permettait de modifier une description : Charles l'a demandé, et il a
 *   fallu passer par la ligne de commande pour le faire.
 */

import { useEffect, useState } from 'react';

import { Champ } from '../gabarits/Champ.tsx';
import { FenetreDeConfirmation } from '../gabarits/FenetreDeConfirmation.tsx';
import { Partie } from '../gabarits/Partie.tsx';
import type { ProjetVu } from './api.ts';

export function SectionFicheDuProjet({
  projet,
  presents,
  peutModifier,
  peutAdministrer,
  surModification,
  surArchivage,
  surSuppression,
}: {
  readonly projet: ProjetVu;
  readonly presents: number;
  readonly peutModifier: boolean;

  /** Le propriétaire, et lui seul : archiver, désarchiver, supprimer. */
  readonly peutAdministrer: boolean;

  surModification: (nom: string, description: string) => Promise<void>;
  surArchivage: (archive: boolean) => void;
  surSuppression: () => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [nom, setNom] = useState(projet.nom);
  const [description, setDescription] = useState(projet.description ?? '');
  const [enCours, setEnCours] = useState(false);

  // Le projet peut changer SOUS l'écran — un autre membre le renomme, et le
  // canal nous l'annonce. Le formulaire fermé doit alors repartir des valeurs
  // à jour ; ouvert, on ne touche à rien : écraser ce que quelqu'un est en
  // train d'écrire serait pire que tout.
  useEffect(() => {
    if (ouvert) return;

    setNom(projet.nom);
    setDescription(projet.description ?? '');
  }, [projet.nom, projet.description, ouvert]);

  return (
    <Partie
      etiquette="Le projet"
      nature="projet"
      titre={projet.nom}
      niveauDeTitre={2}
      data-role="partie-projet"
      enTete={
        <p className="texte-attenue" data-role="presence">
          {presents <= 1
            ? 'Vous êtes seul(e) ici pour l’instant.'
            : `${String(presents)} personnes travaillent ici en ce moment.`}
        </p>
      }
    >
      <h2 className="pour-lecteur-decran" data-role="nom-du-projet">
        {projet.nom}
      </h2>

      {projet.description !== undefined && (
        <p className="description-de-projet" data-role="description">
          {projet.description}
        </p>
      )}

      {projet.archive && (
        <p className="texte-attenue" data-role="archive">
          Ce projet est archivé : il se lit, il ne se modifie plus.
        </p>
      )}

      {peutModifier &&
        (ouvert ? (
          <form
            data-role="formulaire-du-projet"
            onSubmit={(evenement) => {
              evenement.preventDefault();
              void (async () => {
                setEnCours(true);
                await surModification(nom.trim(), description.trim());
                setEnCours(false);
                setOuvert(false);
              })();
            }}
          >
            <Champ
              libelle="Nom du projet"
              name="nom-du-projet"
              value={nom}
              onChange={(evenement) => setNom(evenement.target.value)}
              required
            />
            <Champ
              libelle="Description (facultative)"
              aide="De quoi il retourne, en une ou deux phrases. La vider l’efface."
              name="description-du-projet"
              value={description}
              onChange={(evenement) => setDescription(evenement.target.value)}
            />

            <ul className="barre-de-commandes">
              <li>
                <button
                  type="submit"
                  className="principal"
                  data-role="enregistrer-le-projet"
                  disabled={enCours || nom.trim() === ''}
                >
                  {enCours ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </li>
              <li>
                {/* Annuler ne fait QUE fermer : c'est l'effet ci-dessus qui
                    remet les valeurs du projet dès que le formulaire se
                    referme. Le faire ici AUSSI était redondant — les deux
                    falsifications l'ont montré, chacune passant grâce à
                    l'autre. */}
                <button
                  type="button"
                  data-role="annuler-la-modification"
                  onClick={() => setOuvert(false)}
                >
                  Annuler
                </button>
              </li>
            </ul>
          </form>
        ) : (
          <button type="button" data-role="modifier-le-projet" onClick={() => setOuvert(true)}>
            Modifier le nom et la description
          </button>
        ))}

      {peutAdministrer && (
        <ul className="barre-de-commandes" data-role="commandes-du-projet">
          <li>
            {/* Un seul bouton, qui dit l'état où il MÈNE. Deux boutons dont un
                inerte obligeraient à comparer deux libellés pour savoir où l'on
                en est. */}
            {projet.archive ? (
              <button
                type="button"
                data-role="desarchiver-le-projet"
                onClick={() => surArchivage(false)}
              >
                Désarchiver le projet
              </button>
            ) : (
              <button
                type="button"
                data-role="archiver-le-projet"
                onClick={() => surArchivage(true)}
              >
                Archiver le projet
              </button>
            )}
          </li>
          <li>
            <FenetreDeConfirmation
              libelle="Supprimer le projet"
              titre="Supprimer ce projet ?"
              libelleDeConfirmation="Supprimer le projet"
              data-role="supprimer-le-projet"
              surConfirmation={surSuppression}
            >
              <p>
                Le projet disparaît de votre liste et de celle de toutes les personnes qui y
                travaillent.
              </p>
              <p>
                Rien n’est effacé : les conversations, les documents déposés et les dépenses restent
                enregistrés. Mais personne ne peut le remettre en place aujourd’hui — l’écran qui
                sort un projet de la corbeille n’existe pas encore.
              </p>
            </FenetreDeConfirmation>
          </li>
        </ul>
      )}
    </Partie>
  );
}
